/**
 * Phase 14 — the editor's two master moves, pure: flip a section between
 * "Use master" and "Own settings" (going back to the master re-seeds that
 * section's draft fields from the master's effective config, so nothing
 * stale lingers), and follow a master (campaign + every non-own section).
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  DROP_GEN_SECTIONS,
  readHourOptimizer,
  type DropGenEffectiveConfig,
  type DropGenMasterRef,
  type DropGenSection,
} from '@trafficpulse/shared';

import type { ContainerDraft } from '../../../../drop-generator.types';
import { DEFAULT_SEND, pickSendDefaults, rawSendDefaults } from '../container-draft';
import { creativeToDraft } from '../creative-draft';
import { gatewayToDraft } from '../gateway-draft';
import { gatewayVerdictToDraft } from '../satellites/gateway-verdict-draft';
import { hitAgainToDraft } from '../satellites/hit-again-draft';
import { scalingToDraft } from '../satellites/scaling-draft';
import { verdictToDraft } from '../satellites/verdict-draft';

export function reseedSection(draft: ContainerDraft, section: DropGenSection, m: DropGenEffectiveConfig): ContainerDraft {
  const g = gatewayToDraft(m.gateway);
  const defaults = (m.creative?.dropDefaultsJson ?? {}) as Record<string, unknown>;
  switch (section) {
    case 'gatewayStrategy':
      return {
        ...draft,
        gateway: {
          ...draft.gateway,
          strategy: g.strategy,
          orderMode: g.orderMode,
          weightsMode: g.weightsMode,
          ranking: draft.kind === 'ROUTE_TEST' ? draft.gateway.ranking : g.ranking,
        },
      };
    case 'gatewayPool':
      return {
        ...draft,
        gateway: {
          ...draft.gateway,
          pool: g.pool,
          senderDefaults: g.senderDefaults,
        },
      };
    case 'creative':
      return { ...draft, creative: creativeToDraft(m.creative) };
    case 'options':
      return {
        ...draft,
        sendDefaults: { ...DEFAULT_SEND, ...pickSendDefaults(defaults) },
        sendDefaultsRaw: rawSendDefaults(defaults),
      };
    case 'verdict':
      return {
        ...draft,
        verdict: verdictToDraft(m.verdict),
        scaling: scalingToDraft(m.verdict),
        scalingEnabled: m.scalingEnabled,
        gatewayVerdict: gatewayVerdictToDraft(m.verdict),
        hitAgain: hitAgainToDraft(m.verdict),
      };
    case 'hourOptimizer':
      return {
        ...draft,
        hourOptimizer: readHourOptimizer(m.hourOptimizerJson),
      };
    case 'amount':
      return { ...draft, defaultAmount: m.defaultAmount };
    case 'failMode':
      return { ...draft, failMode: m.failMode };
    case 'autoSend':
      return draft; // saved on its own, never through the draft (Phase 20)
  }
}

/** "Own settings" keeps the current values as a starting point; "Use
 *  master" drops the flag and shows the master's values again. */
export function applySectionSource(
  draft: ContainerDraft,
  change: {
    section: DropGenSection;
    own: boolean;
    master: DropGenEffectiveConfig | null;
  },
): ContainerDraft {
  const ownSections = { ...draft.ownSections };
  if (change.own) ownSections[change.section] = true;
  else delete ownSections[change.section];
  const next = { ...draft, ownSections };
  return change.own || !change.master ? next : reseedSection(next, change.section, change.master);
}

/** Follow a master config: every section not kept as own. The campaign is
 *  the container's own — `campaign` (when given) is the one picked inside
 *  the master config's group by the caller, who knows the group. */
export function applyMaster(
  draft: ContainerDraft,
  master: DropGenMasterRef | null,
  config: DropGenEffectiveConfig | null,
  campaign?: number | null,
): ContainerDraft {
  if (!master) return { ...draft, masterId: null, ownSections: {} };
  let next: ContainerDraft = {
    ...draft,
    masterId: master.id,
    ...(campaign !== undefined ? { campaignWiserId: campaign } : {}),
  };
  if (config) for (const section of DROP_GEN_SECTIONS) if (draft.ownSections[section] !== true) next = reseedSection(next, section, config);
  return next;
}

/** The draft fields one section covers — what "overridden" is judged on. */
export function sectionSlice(draft: ContainerDraft, section: DropGenSection): unknown {
  switch (section) {
    case 'gatewayStrategy':
      return [
        draft.gateway.strategy,
        draft.gateway.orderMode,
        draft.gateway.weightsMode,
        ...(draft.kind === 'ROUTE_TEST' ? [] : [draft.gateway.ranking]),
      ];
    case 'gatewayPool':
      return [draft.gateway.pool, draft.gateway.senderDefaults];
    case 'creative':
      return draft.creative;
    case 'options':
      return [draft.sendDefaults, draft.sendDefaultsRaw];
    case 'verdict':
      return [draft.verdict, draft.scaling, draft.scalingEnabled, draft.gatewayVerdict, draft.hitAgain];
    case 'hourOptimizer':
      return draft.hourOptimizer;
    case 'amount':
      return draft.defaultAmount;
    case 'failMode':
      return draft.failMode;
    case 'autoSend':
      return null;
  }
}

const sameAsMaster = (draft: ContainerDraft, s: DropGenSection, master: DropGenEffectiveConfig) =>
  JSON.stringify(sectionSlice(draft, s)) === JSON.stringify(sectionSlice(reseedSection(draft, s, master), s));

/** A following section whose values no longer match the master's was
 *  edited: it is overridden for this container (flagged on the first edit). */
export function editedSections(draft: ContainerDraft, master: DropGenEffectiveConfig): DropGenSection[] {
  return DROP_GEN_SECTIONS.filter((s) => draft.ownSections[s] !== true && !sameAsMaster(draft, s, master));
}

/** The way back: of the sections an edit made own (`edited`), those whose values
 *  match the master's again follow it again — changing a value and changing it
 *  back leaves no "Own value" (user, 12/10/2026). A section saved as own, or set
 *  to "Own settings" by hand, is never among `edited`. */
export function matchingSections(
  draft: ContainerDraft,
  master: DropGenEffectiveConfig,
  edited: Iterable<DropGenSection>,
): DropGenSection[] {
  return [...edited].filter((s) => draft.ownSections[s] === true && sameAsMaster(draft, s, master));
}
